/**
 * BehavioralTracker - Behavioral Event Tracking
 *
 * Tracks user behavior on e-commerce stores to build intelligence:
 * - Product views, category browsing
 * - Cart actions (add, remove, update)
 * - Search queries and result clicks
 * - Wishlist interactions
 * - Engagement metrics (scroll, time on page)
 *
 * In v6, cart/page/product auto-tracking is handled by MicroAdapter events
 * wired in HamlaSDK. BehavioralTracker keeps interaction tracking (scroll,
 * clicks, time) and the public tracking API for manual merchant usage.
 *
 * Usage:
 * ```typescript
 * const tracker = new BehavioralTracker(eventTracker, { debug: true });
 *
 * // Manual tracking:
 * tracker.trackProductView({ id: '123', name: 'Product', price: 99.99 });
 * tracker.trackAddToCart({ id: '123', name: 'Product' }, 2);
 * tracker.trackSearch('shoes', 42);
 * ```
 */

import { Logger } from '../core/Logger';
import type { EventTracker } from './EventTracker';
import type {
  BehavioralEventType,
  ProductContext,
  CartContext,
  InteractionContext,
  TrackingEvent,
} from '../types/sdk.types';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_SCHEMA_VERSION,
  SCROLL_MILESTONES,
  isEngaged,
  findActionable,
  describeElement,
} from './events';

/**
 * Configuration for BehavioralTracker
 */
export interface BehavioralTrackerConfig {
  /** Enable debug logging */
  debug?: boolean;

  /** Track scroll depth */
  trackScrollDepth?: boolean;

  /** Track time on page */
  trackTimeOnPage?: boolean;

  /**
   * Capture generic interactions (clicks on actionable elements, form
   * submits). Structure only — never user-typed values. Default true.
   */
  trackInteractions?: boolean;

  /**
   * How often (ms) to emit an engagement_ping while the tab is visible, so a
   * long-dwell session is captured even if it never goes hidden. 0 disables
   * the periodic ping. Default 15000.
   */
  engagementPingIntervalMs?: number;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: Required<BehavioralTrackerConfig> = {
  debug: false,
  trackScrollDepth: true,
  trackTimeOnPage: true,
  trackInteractions: true,
  engagementPingIntervalMs: 15000,
};

/**
 * Cart data accepted by trackCartView.
 * Mirrors the fields used from the platform Cart type.
 */
export interface CartViewData {
  total: number;
  currency: string;
  itemCount: number;
  items?: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
    image?: string;
  }>;
}

/**
 * BehavioralTracker - Behavioral event tracking
 */
export class BehavioralTracker {
  private logger: Logger;
  private config: Required<BehavioralTrackerConfig>;
  private eventTracker: EventTracker;

  // Sequence tracking
  private sequenceNumber = 0;

  // Page interaction tracking
  private pageLoadTime: number = 0;
  private maxScrollDepth: number = 0;
  private clickCount: number = 0;

  // Engagement tracking: active (foreground) time, one-shot scroll milestones,
  // and terminal-session guard.
  private activeMs: number = 0;
  private lastVisibleStart: number = 0;
  private reachedMilestones: Set<number> = new Set();
  private sessionEnded: boolean = false;
  private pingTimer: ReturnType<typeof setInterval> | null = null;

  // Tracking state
  private isDestroyed = false;
  private scrollHandler: (() => void) | null = null;
  private clickHandler: ((event: Event) => void) | null = null;
  private submitHandler: ((event: Event) => void) | null = null;
  private visibilityHandler: (() => void) | null = null;
  private pageHideHandler: (() => void) | null = null;

  // Callback when a real purchase is detected — set via onConversion(), called by HamlaSDK
  // @ts-expect-error Set via onConversion(), called externally when conversion detected
  private conversionCallback: (() => void) | null = null;

  constructor(
    eventTracker: EventTracker,
    config: BehavioralTrackerConfig = {}
  ) {
    this.eventTracker = eventTracker;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.logger = new Logger('[BehavioralTracker]');

    if (this.config.debug) {
      this.logger.info('BehavioralTracker initialized');
    }

    // Setup interaction tracking
    this.setupInteractionTracking();
  }

  /**
   * Register callback for when a real purchase is detected (order confirmation page)
   */
  onConversion(callback: () => void): void {
    this.conversionCallback = callback;
  }

  // ==========================================
  // Interaction Tracking
  // ==========================================

  /**
   * Setup scroll and click tracking
   */
  private setupInteractionTracking(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    this.pageLoadTime = Date.now();
    this.lastVisibleStart = Date.now();

    // Track scroll depth + emit a one-shot milestone as each threshold passes.
    if (this.config.trackScrollDepth) {
      this.scrollHandler = () => {
        const scrollHeight =
          document.documentElement.scrollHeight - window.innerHeight;
        if (scrollHeight <= 0) return;
        const scrollPercent = Math.min(
          100,
          Math.round((window.scrollY / scrollHeight) * 100)
        );
        this.maxScrollDepth = Math.max(this.maxScrollDepth, scrollPercent);
        for (const milestone of SCROLL_MILESTONES) {
          if (
            this.maxScrollDepth >= milestone &&
            !this.reachedMilestones.has(milestone)
          ) {
            this.reachedMilestones.add(milestone);
            this.trackEvent(ANALYTICS_EVENTS.SCROLL_MILESTONE, {
              metadata: { depth: milestone, v: ANALYTICS_SCHEMA_VERSION },
            });
          }
        }
      };
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    // Track clicks: a raw count (for the engaged-session signal) plus a generic
    // element_click capture on actionable elements. Capture phase so we still
    // see the click if the site calls stopPropagation.
    this.clickHandler = (event: Event) => {
      this.clickCount++;
      this.captureClick(event);
    };
    document.addEventListener('click', this.clickHandler, true);

    // Track form submissions (a strong conversion hint on lead/contact/booking
    // forms) — structure only, never field values.
    if (this.config.trackInteractions) {
      this.submitHandler = (event: Event) => this.captureSubmit(event);
      document.addEventListener('submit', this.submitHandler, true);
    }

    // Reliable delivery: visibilitychange → hidden is the ONLY "leaving" signal
    // mobile browsers fire dependably, so we snapshot engagement there and
    // beacon it out; pagehide sends the terminal session_end. 'beforeunload' is
    // deliberately NOT used — mobile drops it, which is exactly why engagement
    // data never arrived before.
    if (this.config.trackTimeOnPage) {
      this.visibilityHandler = () => {
        if (typeof document === 'undefined') return;
        if (document.visibilityState === 'hidden') {
          this.accumulateActiveTime();
          this.lastVisibleStart = 0; // stop counting while hidden
          this.trackEngagementPing(false);
          this.eventTracker.flushViaBeacon();
        } else {
          this.lastVisibleStart = Date.now(); // resume counting
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);

      this.pageHideHandler = () => this.endSession();
      window.addEventListener('pagehide', this.pageHideHandler);

      // Periodic ping so a long-dwell, never-hidden session is still captured;
      // the auto-flush timer delivers it.
      const interval = this.config.engagementPingIntervalMs;
      if (interval > 0) {
        this.pingTimer = setInterval(() => {
          if (
            typeof document !== 'undefined' &&
            document.visibilityState === 'visible'
          ) {
            this.trackEngagementPing(false);
          }
        }, interval);
      }
    }
  }

  /**
   * Get current interaction metrics
   */
  private getInteractionMetrics(): InteractionContext {
    const durationSeconds = Math.round((Date.now() - this.pageLoadTime) / 1000);

    return {
      durationSeconds,
      scrollDepth: this.maxScrollDepth,
      clicks: this.clickCount,
    };
  }

  /**
   * Accumulate active (foreground) time since the tab last became visible.
   * Advances the marker so time is never double-counted, and no-ops while the
   * tab is hidden (lastVisibleStart === 0).
   */
  private accumulateActiveTime(): void {
    if (this.lastVisibleStart > 0) {
      this.activeMs += Date.now() - this.lastVisibleStart;
      this.lastVisibleStart = Date.now();
    }
  }

  /**
   * Emit an engagement_ping snapshot: active time, max scroll depth, click
   * count, and whether the session is "engaged" so far.
   */
  private trackEngagementPing(final: boolean): void {
    this.accumulateActiveTime();
    const engaged = isEngaged({
      activeMs: this.activeMs,
      scrollDepth: this.maxScrollDepth,
      interactions: this.clickCount,
    });
    this.trackEvent(ANALYTICS_EVENTS.ENGAGEMENT_PING, {
      interaction: this.getInteractionMetrics(),
      metadata: {
        activeMs: this.activeMs,
        engaged,
        final,
        v: ANALYTICS_SCHEMA_VERSION,
      },
    });
  }

  /**
   * Terminal session end — fires once (on pagehide) and beacons out whatever
   * is queued so the final engagement state is never lost.
   */
  private endSession(): void {
    if (this.sessionEnded) return;
    this.sessionEnded = true;
    this.accumulateActiveTime();
    this.trackSessionEnd();
    this.eventTracker.flushViaBeacon();
  }

  /**
   * Capture a generic click on an actionable element (CTA, link, button) —
   * structure only, no user input. Never throws: a tracking bug must never
   * break the host page's own click handling.
   */
  private captureClick(event: Event): void {
    if (!this.config.trackInteractions) return;
    try {
      const actionable = findActionable(event.target);
      if (!actionable) return;
      this.trackEvent(ANALYTICS_EVENTS.ELEMENT_CLICK, {
        metadata: {
          ...describeElement(actionable),
          kind: 'click',
          v: ANALYTICS_SCHEMA_VERSION,
        },
      });
    } catch {
      /* never break the host page */
    }
  }

  /**
   * Capture a form submission — the form's identity and field COUNT only,
   * never field values.
   */
  private captureSubmit(event: Event): void {
    if (!this.config.trackInteractions) return;
    try {
      const form = event.target as
        | (Element & { elements?: { length: number } })
        | null;
      if (!form || (form.tagName || '').toLowerCase() !== 'form') return;
      const desc = describeElement(form);
      this.trackEvent(ANALYTICS_EVENTS.FORM_SUBMIT, {
        metadata: {
          selector: desc.selector,
          id: desc.id,
          name: desc.name,
          action:
            typeof form.getAttribute === 'function'
              ? form.getAttribute('action') || undefined
              : undefined,
          fieldCount: form.elements ? form.elements.length : undefined,
          v: ANALYTICS_SCHEMA_VERSION,
        },
      });
    } catch {
      /* never break the host page */
    }
  }

  // ==========================================
  // Public Tracking Methods
  // ==========================================

  /**
   * Track a product view
   */
  trackProductView(product: ProductContext): void {
    this.trackEvent('view_product', { product });
  }

  /**
   * Track a category/collection view
   */
  trackCategoryView(categoryId: string, categoryName?: string): void {
    this.trackEvent('view_category', {
      product: { category: categoryName },
      metadata: { categoryId },
    });
  }

  /**
   * Track a collection view
   */
  trackCollectionView(collectionId: string, collectionName?: string): void {
    this.trackEvent('view_collection', {
      metadata: { collectionId, collectionName },
    });
  }

  /**
   * Track add to cart
   */
  trackAddToCart(product: ProductContext, quantity: number = 1): void {
    this.trackEvent('add_to_cart', {
      product,
      metadata: { quantity },
    });
  }

  /**
   * Track remove from cart
   */
  trackRemoveFromCart(product: ProductContext, quantity: number = 1): void {
    this.trackEvent('remove_from_cart', {
      product,
      metadata: { quantity },
    });
  }

  /**
   * Track cart view
   */
  trackCartView(cart: CartViewData): void {
    this.trackEvent('view_cart', {
      cart: {
        value: cart.total,
        itemCount: cart.itemCount,
        currency: cart.currency,
        items: cart.items?.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          imageUrl: item.image,
        })),
      },
    });
  }

  /**
   * Track checkout intent (about to checkout)
   */
  trackCheckoutIntent(): void {
    this.trackEvent('initiate_checkout', {
      interaction: this.getInteractionMetrics(),
    });
  }

  /**
   * Track checkout completion
   */
  trackCheckoutComplete(orderId: string, orderValue: number, currency: string): void {
    this.trackEvent('complete_checkout', {
      cart: { value: orderValue, currency },
      metadata: { orderId },
    });
  }

  /**
   * Track search query
   */
  trackSearch(query: string, resultCount?: number): void {
    this.trackEvent('search', {
      metadata: { query, resultCount },
    });
  }

  /**
   * Track search result click
   */
  trackSearchResultClick(query: string, product: ProductContext, position: number): void {
    this.trackEvent('search_result_click', {
      product,
      metadata: { query, position },
    });
  }

  /**
   * Track filter application
   */
  trackFilterApply(filterType: string, filterValue: string): void {
    this.trackEvent('filter_apply', {
      metadata: { filterType, filterValue },
    });
  }

  /**
   * Track sort change
   */
  trackSortChange(sortBy: string, sortOrder: 'asc' | 'desc'): void {
    this.trackEvent('sort_change', {
      metadata: { sortBy, sortOrder },
    });
  }

  /**
   * Track wishlist add
   */
  trackWishlistAdd(product: ProductContext): void {
    this.trackEvent('wishlist_add', { product });
  }

  /**
   * Track wishlist remove
   */
  trackWishlistRemove(product: ProductContext): void {
    this.trackEvent('wishlist_remove', { product });
  }

  /**
   * Track image zoom
   */
  trackImageZoom(product: ProductContext): void {
    this.trackEvent('zoom_image', { product });
  }

  /**
   * Track reviews view
   */
  trackViewReviews(product: ProductContext): void {
    this.trackEvent('view_reviews', { product });
  }

  /**
   * Track product comparison
   */
  trackCompareProducts(productIds: string[]): void {
    this.trackEvent('compare_products', {
      metadata: { productIds },
    });
  }

  /**
   * Track product share
   */
  trackShareProduct(product: ProductContext, platform: string): void {
    this.trackEvent('share_product', {
      product,
      metadata: { platform },
    });
  }

  /**
   * Track link copy
   */
  trackCopyLink(url: string): void {
    this.trackEvent('copy_link', {
      metadata: { url },
    });
  }

  /**
   * Track page view
   */
  trackPageView(pageType: string, url?: string): void {
    this.trackEvent('page_view', {
      metadata: {
        pageType,
        url: url || (typeof window !== 'undefined' ? window.location.href : undefined),
      },
    });
  }

  /**
   * Track session start
   */
  trackSessionStart(): void {
    this.sequenceNumber = 0;
    this.pageLoadTime = Date.now();
    this.maxScrollDepth = 0;
    this.clickCount = 0;
    this.activeMs = 0;
    this.lastVisibleStart = Date.now();
    this.reachedMilestones.clear();
    this.sessionEnded = false;

    this.trackEvent('session_start', {});
  }

  /**
   * Track session end (called on page unload)
   */
  trackSessionEnd(): void {
    this.trackEvent('session_end', {
      interaction: this.getInteractionMetrics(),
      metadata: {
        activeMs: this.activeMs,
        engaged: isEngaged({
          activeMs: this.activeMs,
          scrollDepth: this.maxScrollDepth,
          interactions: this.clickCount,
        }),
        v: ANALYTICS_SCHEMA_VERSION,
      },
    });
  }

  // ==========================================
  // Core Tracking
  // ==========================================

  /**
   * Track a behavioral event
   *
   * NOTE: Consent is the merchant's responsibility, not Hamla's.
   * We track all events like Google Analytics does.
   */
  private trackEvent(
    type: BehavioralEventType,
    data: {
      product?: ProductContext;
      cart?: CartContext;
      interaction?: InteractionContext;
      metadata?: Record<string, unknown>;
    }
  ): void {
    if (this.isDestroyed) return;

    const event: TrackingEvent = {
      type,
      timestamp: Date.now(),
      sequenceNumber: ++this.sequenceNumber,
      product: data.product,
      cart: data.cart,
      interaction: data.interaction,
      metadata: data.metadata,
    };

    if (this.config.debug) {
      this.logger.info(`Tracking ${type}`, {
        sequenceNumber: event.sequenceNumber,
        product: data.product?.id,
        cart: data.cart?.value,
      });
    }

    this.eventTracker.track(event);
  }

  // ==========================================
  // Lifecycle
  // ==========================================

  /**
   * Destroy the tracker and cleanup
   */
  destroy(): void {
    if (this.isDestroyed) return;

    this.isDestroyed = true;

    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }

    // Remove event listeners (capture flag must match addEventListener).
    if (typeof window !== 'undefined') {
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
      }
      if (this.clickHandler) {
        document.removeEventListener('click', this.clickHandler, true);
      }
      if (this.submitHandler) {
        document.removeEventListener('submit', this.submitHandler, true);
      }
      if (this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
      }
      if (this.pageHideHandler) {
        window.removeEventListener('pagehide', this.pageHideHandler);
      }
    }

    if (this.config.debug) {
      this.logger.info('BehavioralTracker destroyed');
    }
  }

  /**
   * Get current sequence number (for debugging)
   */
  getSequenceNumber(): number {
    return this.sequenceNumber;
  }
}
